<template>
  <div
    ref="itemRef"
    :class="[
      'transition-opacity duration-500 ease-out cursor-pointer',
      isVisible ? 'opacity-100' : 'opacity-0',
      mode === 'schedule'
        ? 'flex flex-row justify-between items-center bg-white rounded-2xl p-4 md:hover:shadow-sm border border-gray-100 shadow-sm'
        : 'flex flex-row justify-between items-center rounded-lg md:border md:p-4 md:hover:bg-gray-50'
    ]"
    :style="{ transitionDelay: `${animationDelay}ms` }"
    @click="mode==='schedule' ? (isSelected ? addItem() : onClickShopItem()) : () => {}"
  >
    <img
      v-if="product?.images && product?.images.length && mode !== 'schedule'"
      :src="product.images[0]"
      alt="service image"
      :class="['me-3 rounded-xl', size==='small' ? 'w-14 h-14' : 'w-20 h-20 ']">
    <img
      v-else-if="product?.images && product?.images.length"
      :src="product.images[0]"
      alt="service image"
      :class="['me-3 rounded-xl object-cover flex-none', size==='small' ? 'w-12 h-12' : 'w-14 h-14']">
    <div class="flex w-full flex-col h-full items-start min-w-0">
      <span :class="['font-medium', mode === 'schedule' ? 'text-base' : '', size === 'small' ? 'text-sm' : '']">{{ product?.name }}</span>
      <span :class="['text-gray-500 truncate w-full', size === 'small' ? 'text-xs' : 'text-sm']">{{ product.categories.join(', ')
        }}</span>
      <div v-if="mode !== 'payment'" class="flex w-full flex-col items-start">
        <span v-if="!product.discountedPrice"
              :class="['flex-none', mode === 'schedule' ? 'font-medium mt-2' : 'mt-auto pt-2', size === 'small' ? 'text-sm' : '']">₪{{ product.price
          }}</span>
        <div v-else :class="['flex-none flex gap-2 items-center', mode === 'schedule' ? 'mt-2' : 'mt-auto pt-2 ', size === 'small' ? 'text-sm' : '']">
          <span class="font-medium text-purple-400">₪{{ product.discountedPrice }}</span>
          <span class="text-sm line-through">₪{{ product.price }}</span>
        </div>
      </div>
    </div>
    <div v-if="mode==='schedule'" v-wave
         class="border rounded-full flex justify-center items-center w-9 h-9 flex-none ms-3 transition-colors"
         :style="{
      background: isSelected ? colors.leadColor : 'transparent',
      borderColor: colors.darkenColor
    }"
    >
      <i :class="['pi !text-sm', isSelected ? 'pi-minus' : 'pi-plus']"
         :style="{color: isSelected ? colors.fontColor : colors.darkenColor }" />
    </div>

    <div v-if="mode==='checkout'" class="flex gap-2 justify-center items-center">
      <div @click="increase"
           class="aspect-square !w-8 !h-8 flex justify-center items-center"
      >
        <i class="pi pi-plus !text-sm" :style="`color:${colors.leadColor}`" />
      </div>
      <span>{{ amount }}</span>
      <div @click="decrease"
           class="aspect-square !w-8 !h-8 flex justify-center items-center"
      >
        <i class="pi pi-minus !text-sm" :style="`color:${colors.leadColor}`" />
      </div>
    </div>

    <div v-if="mode === 'payment'" class="flex flex-col items-start">
      <span v-if="!product.discountedPrice"
            :class="['flex-none my-auto ', size === 'small' ? 'text-sm' : '']">₪{{ product.price }}</span>
      <div v-else :class="['flex-none flex gap-2 items-center mt-auto pt-2 ', size === 'small' ? 'text-sm' : '']">
        <span class="font-medium text-purple-400">₪{{ product.discountedPrice }}</span>
        <span class="text-sm line-through">₪{{ product.price }}</span>
      </div>
    </div>

  </div>

</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, PropType, ref } from 'vue';
import { IShopItem, TCartItem } from '@torim-app/types';
import { useDrawer } from '../composables/useDrawer';
import ProductItemDetails from './ProductItemDetails.vue';

const props = defineProps({
  product: {
    type: Object as PropType<IShopItem>,
    required: true
  },
  size: {
    type: String as PropType<'small' | 'normal'>,
    required: false,
    default: 'normal'
  },
  selectedProducts: {
    type: Array as PropType<TCartItem[]>,
    required: false,
    default: () => []
  },
  mode: {
    type: String as PropType<'checkout' | 'schedule' | 'payment'>,
    required: false,
    default: 'schedule'
  },
  animationDelay: {
    type: Number,
    required: false,
    default: 0
  },
  colors: {
    type: Object as PropType<{
      lighterColor: string;
      leadColor: string;
      fontColor: string;
      darkenColor: string;
    }>,
    required: false,
    default: () => ({
      lighterColor: '#d6d6d6',
      leadColor: '#000000',
      fontColor: '#000000',
      darkenColor: '#000000'
    })
  },
  amount: {
    type: Number,
    required: false,
    default: 0
  }
});

const emit = defineEmits(['chooseProduct', 'changeAmount', 'removeProduct']);
const { openDrawer, closeDrawer } = useDrawer();

const itemRef = ref<HTMLDivElement>();
const isVisible = ref(false);
let observer: IntersectionObserver | null = null;

const isSelected = computed(() => {
  return !!props.selectedProducts.find(x => x.id === props.product?.id);
});

const onClickShopItem = () => {
  openDrawer(ProductItemDetails, {
    selectedItem: props.product,
    showAddToCart: true,
    isSelected: isSelected,
    onAddToCart: addItem
  },{
    noPadding: true,
    closable: true,
    title: props.product?.name
  });
};

const increase = () => {
  emit('changeAmount', { itemId: props.product?.id, amount: 1 });
};

const decrease = () => {
  emit('changeAmount', { itemId: props.product?.id, amount: -1 });
};

const addItem = () => {
  isSelected.value ? emit('removeProduct', props.product?.id) : emit('chooseProduct', props.product?.id);
  closeDrawer();
};

onMounted(() => {
  if (!itemRef.value) return;

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !isVisible.value) {
          isVisible.value = true;
          observer?.disconnect();
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    }
  );

  observer.observe(itemRef.value);
});

onUnmounted(() => {
  observer?.disconnect();
});

</script>

<style scoped lang="scss">
.shop-item-details {
  .carousel__pagination {
    @apply absolute left-1/2 -translate-x-1/2 bottom-4;
  }

  .carousel__pagination-button::after {
    @apply rounded bg-gray-300 w-2;
  }

  .carousel__pagination-button:hover::after, .carousel__pagination-button--active::after {
    @apply bg-purple-500 rounded w-5;
  }

}
</style>
